<script setup lang="ts">
import { computed } from 'vue'
import { boolean } from 'yup'

defineOptions({ name: 'UiButton' })

type ButtonVariant = 'filled' | 'outline' | 'secondary'

const props = defineProps({
  label: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  variant: { type: String as () => ButtonVariant, default: 'filled' },
  size: { type: String as () => 'sm' | 'md' | 'lg', default: 'md' },
  isAdmin: { type: boolean },
})

const emit = defineEmits(['click'])

const isDisabled = computed(() => props.disabled || props.loading)

// dynamic classes for variant and size
const buttonClasses = computed(() => {
  const base =
    'flex items-center justify-center gap-2 font-medium rounded-xl transition focus:outline-none cursor-pointer '
  const sizeClasses = {
    sm: 'h-8 px-3 text-sm',
    md: 'h-[47px] px-5',
    lg: 'h-12 px-6 text-lg',
  }
  const variantClasses = {
    filled: props.isAdmin
      ? 'bg-[#2d5396] text-white hover:opacity-[.9] disabled:bg-[#f9f9f9] disabled:text-[#00000042]'
      : 'bg-[#ffbf00] text-black hover:bg-[#e6ac00] disabled:bg-[#f9f9f9] disabled:text-[#00000042]',
    outline: props.isAdmin
      ? 'border-2 border-[#2d5396] text-[#2d5396] hover:bg-[#D5E2F1] hover:border-black disabled:border-gray-300 disabled:text-gray-400 disabled:bg-gray-100'
      : 'border border-[#ffbf00] text-[#ffbf00] hover:bg-[#ffbf00] hover:text-black disabled:border-gray-300 disabled:text-gray-400 disabled:bg-gray-100',
    secondary: 'bg-gray-200 text-black hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400',
  }
  const disabledClasses = isDisabled.value ? 'opacity-[0.8] cursor-not-allowed' : ''
  return [base, sizeClasses[props.size], variantClasses[props.variant], disabledClasses].join(' ')
})

const handleClick = (e: MouseEvent) => {
  if (!isDisabled.value) emit('click', e)
}
</script>

<template>
  <button :class="buttonClasses" :disabled="isDisabled" @click="handleClick" class="disabled:cursor-not-allowed!">
    <!-- loading spinner -->
    <span v-if="loading" class="animate-spin border-2 border-white border-t-transparent rounded-full w-5 h-5"></span>

    <!-- label / slot -->
    <span v-if="$slots.default">
      <slot />
    </span>
    <span v-else>
      {{ label }}
    </span>
  </button>
</template>

<style scoped></style>
